import { useState, useEffect } from 'react';
import { SectorAnchorsPanel } from './SectorAnchorsPanel';
import { CopilotChat } from './CopilotChat';

// Compact share actions shown on the assessment detail header. Copies the public
// /r/<id> link and offers a WhatsApp deep-link when we already know the phone number.
function ShareReportActions({
  reportId,
  leadName,
  leadPhone,
  hasReport,
}: {
  reportId: string;
  leadName?: string;
  leadPhone?: string;
  hasReport: boolean;
}) {
  const [copied, setCopied] = useState(false);
  if (!hasReport) {
    return (
      <p className="mt-2 text-xs text-gray-400 italic">Report non ancora generato</p>
    );
  }
  const reportUrl = `${window.location.origin}/r/${reportId}`;
  const greetingName = leadName?.split(' ')[0] || '';
  const whatsappMessage = encodeURIComponent(
    `Ciao${greetingName ? ' ' + greetingName : ''}, ecco il report personalizzato di NextProcess: ${reportUrl}`
  );
  // Normalize phone to digits only (WhatsApp deep link format)
  const phoneDigits = leadPhone?.replace(/\D/g, '');
  const waHref = phoneDigits
    ? `https://wa.me/${phoneDigits}?text=${whatsappMessage}`
    : `https://wa.me/?text=${whatsappMessage}`;

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(reportUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be blocked (e.g. insecure context) — fall back to prompt
      window.prompt('Copia il link:', reportUrl);
    }
  }

  return (
    <div className="mt-3 flex flex-col gap-1.5 items-end">
      <button
        onClick={handleCopy}
        className="text-xs px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
      >
        {copied ? '✓ Copiato' : '🔗 Copia link report'}
      </button>
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs px-2 py-1 rounded border border-green-300 bg-green-50 hover:bg-green-100 text-green-700"
      >
        💬 Invia via WhatsApp
      </a>
    </div>
  );
}

interface SalesIntelligence {
  softwareSituation: {
    hasCustomSoftware: boolean;
    hasLegacySystems: boolean;
    multipleDisconnectedTools: boolean;
    toolsCount: number;
    toolsMentioned: string[];
  };
  intentSignals: {
    lookingToDevelop: boolean;
    wantsConsolidation: boolean;
    needsMobileApp: boolean;
    needsIntegrations: boolean;
    expressedFrustration: boolean;
  };
  aiStatus: {
    currentlyUsingAi: boolean;
    aiToolsMentioned: string[];
    aiReadiness: 'none' | 'curious' | 'concerned' | 'ready';
    privacyConcerns: boolean;
    complianceRequirements: string[];
  };
  budgetSignals: {
    mentionedBudget: boolean;
    priceSensitive: boolean;
    failedInvestments: boolean;
    estimatedCurrentSpend: number;
    willingnessToInvest: 'low' | 'medium' | 'high' | 'unknown';
  };
  // New Phase 3 strategic intelligence fields
  decisionMaking?: {
    identified: boolean;
    role: string;
    isCurrentContact: boolean;
    otherStakeholders: string[];
    approvalProcess: 'individual' | 'committee' | 'board' | 'unknown';
  };
  triggerEvent?: {
    hasUrgency: boolean;
    trigger: string;
    deadline?: string;
    externalPressure: boolean;
  };
  pastAttempts?: {
    hasPreviousFailures: boolean;
    failureReasons: string[];
    lessonsLearned: string[];
    skepticismLevel: 'low' | 'medium' | 'high';
  };
  implementationCapacity?: {
    hasInternalResources: boolean;
    technicalLead?: string;
    needsFullService: boolean;
  };
  successMetrics?: string[];
  opportunityScore: number;
  recommendedApproach: string;
  keyTalkingPoints: string[];
  suggestedSolutions: string[];
  // LLM-generated sales action items
  followUpQuestions?: string[];
  painPointsForSales?: string[];
  urgencyFactors?: string[];
  objectionHandlers?: Array<{ objection: string; response: string }>;
}

type AssessmentTypeValue = 'automation' | 'startup' | 'project';

interface AssessmentSummary {
  id: string;
  createdAt: string;
  assessmentType: AssessmentTypeValue;
  lead: {
    name: string;
    email: string;
    company?: string;
  };
  industry: string;
  companySize: string;
  overallScore: number;
  estimatedHoursWasted: number;
  topPainPoint: string;
  topQuickWin: string;
  violations?: ViolationSummary;
}

interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

interface TokenUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  estimatedCostUsd: number;
}

type ViolationType = 'jailbreak' | 'off_topic' | 'harassment' | 'spam';

interface ViolationSummary {
  totalCount: number;
  byType: Record<ViolationType, number>;
  riskScore: number;
  flagged: boolean;
  violations: Array<{
    type: ViolationType;
    message: string;
    timestamp: string;
    severity: 1 | 2 | 3;
  }>;
}

interface AssessmentDetail {
  id: string;
  createdAt: string;
  assessmentType: AssessmentTypeValue;
  lead: {
    name: string;
    email: string;
    company?: string;
    phone?: string;
  };
  formData: Record<string, unknown>;
  scores: Record<string, unknown>;
  metrics: Record<string, unknown>;
  painPoints: string[];
  quickWins: string[];
  recommendations: string[];
  salesIntelligence?: SalesIntelligence;
  conversation?: ConversationMessage[];
  tokenUsage?: TokenUsage;
  status?: 'started' | 'chat_complete' | 'complete';
  violations?: ViolationSummary;
  report?: unknown;
}

// Use relative URLs in production (same origin), localhost only for local dev
const API_URL = (import.meta as unknown as { env: Record<string, string> }).env.VITE_API_URL ??
  (window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');

// Tech stack solutions mapping
const TECH_SOLUTIONS = {
  webApp: {
    name: 'Applicazione Web',
    tech: 'React + Node.js + PostgreSQL',
    description: 'Dashboard o gestionale web custom',
  },
  mobileApp: {
    name: 'App Mobile',
    tech: 'React Native + AWS',
    description: 'App iOS/Android per team sul campo',
  },
  integration: {
    name: 'Integrazione Sistemi',
    tech: 'API + SQS + Serverless',
    description: 'Connessione tra sistemi esistenti',
  },
  automation: {
    name: 'Automazione Processi',
    tech: 'Python + Serverless + S3',
    description: 'Automazione workflow e documenti',
  },
  aiSolution: {
    name: 'Soluzione AI/ML',
    tech: 'Python + AWS + Custom Models',
    description: 'Analisi predittiva, NLP, computer vision',
  },
  dataWarehouse: {
    name: 'Piattaforma Dati',
    tech: 'PostgreSQL + Redis + S3',
    description: 'Centralizzazione e analisi dati',
  },
};

function generateSuggestedSolutions(detail: AssessmentDetail): string[] {
  const solutions: string[] = [];
  const intel = detail.salesIntelligence;

  // Based on pain points and scores
  if ((Number(detail.scores.dataEntry) || 0) <= 2) {
    solutions.push(`${TECH_SOLUTIONS.automation.name}: ${TECH_SOLUTIONS.automation.description} (${TECH_SOLUTIONS.automation.tech})`);
  }

  if ((Number(detail.scores.salesMarketing) || 0) <= 2) {
    solutions.push(`${TECH_SOLUTIONS.webApp.name}: CRM custom per tracking lead (${TECH_SOLUTIONS.webApp.tech})`);
  }

  if (intel?.intentSignals?.needsMobileApp) {
    solutions.push(`${TECH_SOLUTIONS.mobileApp.name}: ${TECH_SOLUTIONS.mobileApp.description} (${TECH_SOLUTIONS.mobileApp.tech})`);
  }

  if (intel?.intentSignals?.needsIntegrations || intel?.softwareSituation?.multipleDisconnectedTools) {
    solutions.push(`${TECH_SOLUTIONS.integration.name}: ${TECH_SOLUTIONS.integration.description} (${TECH_SOLUTIONS.integration.tech})`);
  }

  if (intel?.aiStatus?.aiReadiness === 'curious' || intel?.aiStatus?.aiReadiness === 'ready') {
    solutions.push(`${TECH_SOLUTIONS.aiSolution.name}: ${TECH_SOLUTIONS.aiSolution.description} (${TECH_SOLUTIONS.aiSolution.tech})`);
  }

  if (intel?.intentSignals?.wantsConsolidation) {
    solutions.push(`${TECH_SOLUTIONS.dataWarehouse.name}: ${TECH_SOLUTIONS.dataWarehouse.description} (${TECH_SOLUTIONS.dataWarehouse.tech})`);
  }

  // Default suggestion if none matched
  if (solutions.length === 0) {
    solutions.push(`${TECH_SOLUTIONS.webApp.name}: Gestionale su misura (${TECH_SOLUTIONS.webApp.tech})`);
  }

  return solutions.slice(0, 3);
}

function calculateBudgetEstimate(detail: AssessmentDetail): { wastedCost: number; suggestedBudget: string; roi: string } {
  const hourlyRate = 30; // €30/hour average for Italian SME
  const hoursWasted = (detail.metrics.estimatedHoursWasted as number) || 20;
  const wastedCost = hoursWasted * hourlyRate * 52;

  // Suggest 20-30% of annual waste as initial investment
  const minBudget = Math.round(wastedCost * 0.2 / 1000) * 1000;
  const maxBudget = Math.round(wastedCost * 0.4 / 1000) * 1000;

  const suggestedBudget = `€${minBudget.toLocaleString()} - €${maxBudget.toLocaleString()}`;
  const roiMonths = Math.round((maxBudget / (wastedCost / 12)) * 0.7); // 70% efficiency assumption
  const roi = `${roiMonths}-${roiMonths + 3} mesi`;

  return { wastedCost, suggestedBudget, roi };
}


function ScoreBadge({ score }: { score: number }) {
  const getColor = () => {
    if (score >= 70) return 'bg-green-100 text-green-800';
    if (score >= 50) return 'bg-yellow-100 text-yellow-800';
    if (score >= 30) return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  return (
    <span className={`px-2 py-1 rounded-full text-sm font-medium ${getColor()}`}>
      {score}/100
    </span>
  );
}

function OpportunityBadge({ score }: { score: number }) {
  const getColor = () => {
    if (score >= 8) return 'bg-green-500';
    if (score >= 6) return 'bg-blue-500';
    if (score >= 4) return 'bg-yellow-500';
    return 'bg-gray-400';
  };

  return (
    <div className="flex items-center gap-2">
      <div className={`w-3 h-3 rounded-full ${getColor()}`} />
      <span className="text-sm font-medium">{score}/10 Opportunità</span>
    </div>
  );
}

function CategoryScore({ label, score }: { label: string; score: number }) {
  const clamped = Math.min(5, Math.max(0, score));
  const width = (clamped / 5) * 100;
  const getColor = () => {
    if (clamped >= 4) return 'bg-green-500';
    if (clamped >= 3) return 'bg-blue-500';
    if (clamped >= 2) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="flex items-center gap-3">
      <span className="w-28 text-sm text-gray-600 shrink-0">{label}</span>
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden min-w-0">
        <div className={`h-full ${getColor()} rounded-full`} style={{ width: `${width}%` }} />
      </div>
      <span className="w-8 text-sm font-medium text-gray-700 shrink-0">{clamped.toFixed(1)}/5</span>
    </div>
  );
}

function SignalPill({ active, label }: { active: boolean; label: string }) {
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${
      active ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-400'
    }`}>
      {label}
    </span>
  );
}

function AIReadinessBadge({ level }: { level: string }) {
  const config: Record<string, { color: string; label: string }> = {
    none: { color: 'bg-gray-100 text-gray-600', label: 'Non interessato' },
    curious: { color: 'bg-blue-100 text-blue-800', label: 'Curioso' },
    concerned: { color: 'bg-yellow-100 text-yellow-800', label: 'Preoccupato privacy' },
    ready: { color: 'bg-green-100 text-green-800', label: 'Pronto' },
  };
  const c = config[level] || config.none;
  return <span className={`px-2 py-1 rounded text-xs font-medium ${c.color}`}>{c.label}</span>;
}

function ViolationBadge({ violations }: { violations: ViolationSummary }) {
  if (!violations || violations.totalCount === 0) return null;

  const getColor = () => {
    if (violations.riskScore >= 80) return 'bg-red-600 text-white';
    if (violations.riskScore >= 50) return 'bg-red-500 text-white';
    if (violations.riskScore >= 30) return 'bg-orange-500 text-white';
    return 'bg-yellow-500 text-white';
  };

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-bold ${getColor()}`} title={`Risk: ${violations.riskScore}`}>
      {violations.flagged ? 'SEGNALATO' : violations.totalCount}
    </span>
  );
}

function ViolationTypeBadge({ type }: { type: ViolationType }) {
  const config: Record<ViolationType, { color: string; label: string }> = {
    jailbreak: { color: 'bg-red-100 text-red-800', label: 'Jailbreak' },
    harassment: { color: 'bg-red-100 text-red-800', label: 'Molestie' },
    off_topic: { color: 'bg-yellow-100 text-yellow-800', label: 'Fuori Tema' },
    spam: { color: 'bg-gray-100 text-gray-600', label: 'Spam' },
  };
  const c = config[type];
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.color}`}>{c.label}</span>;
}

function AssessmentTypeBadge({ type }: { type: AssessmentTypeValue }) {
  const config: Record<AssessmentTypeValue, { bg: string; text: string; label: string }> = {
    automation: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Automation' },
    startup: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Startup' },
    project: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Project' },
  };
  const c = config[type] || config.automation;
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

function getDetailScore(detail: AssessmentDetail): number {
  const s = detail.scores;
  if (typeof s.overall === 'number' && s.overall > 0) return Math.round(s.overall as number);
  if (typeof s.composite_score === 'number') return Math.round(s.composite_score as number);
  return 0;
}

function getAdminToken(): string | null {
  return sessionStorage.getItem('adminToken');
}

function adminHeaders(): Record<string, string> {
  const token = getAdminToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

export function AdminPanel() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!sessionStorage.getItem('adminToken');
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [assessments, setAssessments] = useState<AssessmentSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AssessmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'intelligence' | 'followup' | 'conversation' | 'violations' | 'copilot'>('overview');
  const [view, setView] = useState<'assessments' | 'sectors'>('assessments');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setAuthLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setAuthError(data.error || 'Credenziali non valide');
        return;
      }

      const data = await res.json();
      sessionStorage.setItem('adminToken', data.token);
      sessionStorage.setItem('adminEmail', data.email);
      setIsAuthenticated(true);
    } catch {
      setAuthError('Errore di connessione al server');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/admin/logout`, {
        method: 'POST',
        headers: { ...adminHeaders() },
      });
    } catch {
      // Ignore logout errors
    }
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminEmail');
    setIsAuthenticated(false);
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchAssessments();
    }
  }, [isAuthenticated]);

  async function fetchAssessments() {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/assessments`, {
        headers: { ...adminHeaders() },
      });
      if (res.status === 401) {
        // Session expired
        sessionStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminEmail');
        setIsAuthenticated(false);
        return;
      }
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setAssessments(data);
    } catch (err) {
      setError('Failed to load assessments');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchDetail(id: string) {
    try {
      const res = await fetch(`${API_URL}/api/assessments/${id}`, {
        headers: { ...adminHeaders() },
      });
      if (res.status === 401) {
        sessionStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminEmail');
        setIsAuthenticated(false);
        return;
      }
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setDetail(data);
      setSelectedId(id);
    } catch (err) {
      console.error(err);
    }
  }

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-12 bg-automation-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">N</span>
            </div>
            <div>
              <span className="font-semibold text-gray-900 text-xl">NextProcess</span>
              <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded font-medium">Admin</span>
            </div>
          </div>
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setAuthError(null); }}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  authError ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="admin@example.com"
                autoFocus
                required
              />
            </div>
            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setAuthError(null); }}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  authError ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Password"
                required
              />
            </div>
            {authError && (
              <p className="mb-4 text-sm text-red-600 text-center">{authError}</p>
            )}
            <button
              type="submit"
              disabled={authLoading}
              className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
            >
              {authLoading ? 'Accesso...' : 'Accedi'}
            </button>
          </form>
          <div className="mt-4 text-center">
            <a href="/" className="text-sm text-primary-600 hover:underline">
              Torna all'app
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-500">Caricamento...</div>
      </div>
    );
  }

  const budgetEstimate = detail ? calculateBudgetEstimate(detail) : null;
  const suggestedSolutions = detail ? generateSuggestedSolutions(detail) : [];

  // Generate default intelligence if not present
  const intel: SalesIntelligence = detail?.salesIntelligence || {
    softwareSituation: {
      hasCustomSoftware: false,
      hasLegacySystems: true,
      multipleDisconnectedTools: true,
      toolsCount: (Array.isArray(detail?.formData?.softwareTools) ? (detail.formData.softwareTools as string[]).length : 0) || 2,
      toolsMentioned: (Array.isArray(detail?.formData?.softwareTools) ? detail.formData.softwareTools as string[] : []),
    },
    intentSignals: {
      lookingToDevelop: false,
      wantsConsolidation: true,
      needsMobileApp: false,
      needsIntegrations: true,
      expressedFrustration: true,
    },
    aiStatus: {
      currentlyUsingAi: false,
      aiToolsMentioned: [],
      aiReadiness: 'curious',
      privacyConcerns: true,
      complianceRequirements: ['GDPR'],
    },
    budgetSignals: {
      mentionedBudget: false,
      priceSensitive: true,
      failedInvestments: false,
      estimatedCurrentSpend: 5000,
      willingnessToInvest: 'medium',
    },
    opportunityScore: 7,
    recommendedApproach: 'Partire con una demo tecnica delle integrazioni possibili',
    keyTalkingPoints: [
      'Evidenziare il costo delle ore perse',
      'Proporre soluzione modulare',
      'Rassicurare su privacy AI (modelli on-premise disponibili)',
    ],
    suggestedSolutions: [],
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-automation-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">N</span>
            </div>
            <div>
              <span className="font-semibold text-gray-900 text-lg">NextProcess</span>
              <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded font-medium">Pannello Vendite</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-sm">
              <button
                onClick={() => setView('assessments')}
                className={`px-3 py-1.5 ${view === 'assessments' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                Assessments
              </button>
              <button
                onClick={() => setView('sectors')}
                className={`px-3 py-1.5 border-l border-gray-200 ${view === 'sectors' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                Dati settore
              </button>
            </div>
            <a href="/" className="text-sm text-primary-600 hover:underline">
              Torna all'app
            </a>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 hover:text-red-600 transition-colors"
            >
              Esci
            </button>
          </div>
        </div>
      </header>

      {view === 'sectors' ? (
        <main className="max-w-7xl mx-auto">
          <SectorAnchorsPanel />
        </main>
      ) : (
      <main className="max-w-7xl mx-auto px-4 py-4 lg:py-8">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
          {/* List */}
          <div className={`w-full lg:w-1/3 ${selectedId ? 'hidden lg:block' : ''}`}>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              Assessments ({assessments.length})
            </h2>

            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-3 text-sm">{error}</div>
            )}

            {assessments.length === 0 ? (
              <div className="bg-white rounded-lg p-6 text-center text-gray-500 text-sm">
                Nessun assessment
              </div>
            ) : (
              <div className="space-y-1.5">
                {assessments.map((a) => (
                  <div
                    key={a.id}
                    onClick={() => fetchDetail(a.id)}
                    className={`bg-white rounded-lg px-3 py-2.5 cursor-pointer transition-all ${
                      selectedId === a.id
                        ? 'ring-2 ring-primary-500 shadow-sm'
                        : 'hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium text-gray-900 text-sm truncate">{a.lead.name || 'Anonimo'}</p>
                          {a.violations && <ViolationBadge violations={a.violations} />}
                        </div>
                        <p className="text-xs text-gray-500 truncate">{a.lead.company || a.lead.email}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <AssessmentTypeBadge type={a.assessmentType || 'automation'} />
                        <ScoreBadge score={a.overallScore} />
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-gray-400">
                      {a.industry && <span className="bg-gray-100 px-1.5 py-0.5 rounded truncate max-w-[120px]">{a.industry}</span>}
                      {a.companySize && <span className="bg-gray-100 px-1.5 py-0.5 rounded">{a.companySize}</span>}
                      <span className="ml-auto">{new Date(a.createdAt).toLocaleDateString('it-IT')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Detail */}
          <div className={`w-full lg:w-2/3 ${!selectedId ? 'hidden lg:block' : ''}`}>
            {detail ? (
              <div className="space-y-4">
                {/* Mobile back button */}
                <button
                  onClick={() => { setSelectedId(null); setDetail(null); }}
                  className="lg:hidden flex items-center gap-1 text-sm text-primary-600 mb-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  Torna alla lista
                </button>

                {/* Header Card */}
                <div className="bg-white rounded-lg p-4 lg:p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg lg:text-xl font-bold text-gray-900">{detail.lead.name}</h3>
                        <AssessmentTypeBadge type={detail.assessmentType || 'automation'} />
                      </div>
                      <p className="text-gray-500 text-sm">{detail.lead.email}</p>
                      {detail.lead.company && (
                        <p className="text-gray-600 font-medium text-sm">{detail.lead.company}</p>
                      )}
                      {detail.lead.phone && (
                        <p className="text-gray-500 text-xs">{detail.lead.phone}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <OpportunityBadge score={intel.opportunityScore} />
                      <div className="mt-2 text-2xl font-bold text-primary-600">
                        {getDetailScore(detail)}/100
                      </div>
                      <ShareReportActions
                        reportId={detail.id}
                        leadName={detail.lead.name}
                        leadPhone={detail.lead.phone}
                        hasReport={Boolean(detail.report)}
                      />
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="flex gap-2 border-b border-gray-200 overflow-x-auto pb-px -mb-px">
                    <button
                      onClick={() => setActiveTab('overview')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                        activeTab === 'overview'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Panoramica
                    </button>
                    <button
                      onClick={() => setActiveTab('intelligence')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                        activeTab === 'intelligence'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Intelligence Vendite
                    </button>
                    <button
                      onClick={() => setActiveTab('followup')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                        activeTab === 'followup'
                          ? 'border-green-500 text-green-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Follow-up
                    </button>
                    <button
                      onClick={() => setActiveTab('conversation')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                        activeTab === 'conversation'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Conversazione
                    </button>
                    <button
                      onClick={() => setActiveTab('violations')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${
                        activeTab === 'violations'
                          ? 'border-red-500 text-red-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Violazioni
                      {detail.violations && detail.violations.totalCount > 0 && (
                        <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                          {detail.violations.totalCount}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setActiveTab('copilot')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${
                        activeTab === 'copilot'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <span className="w-4 h-4 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full inline-flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </span>
                      Copilot
                    </button>
                  </div>
                </div>

                {activeTab === 'overview' && (
                  <>
                    {/* Company/Form Info - renders key form fields generically */}
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">
                        {(detail.assessmentType || 'automation') === 'automation' ? 'Profilo Azienda' :
                         detail.assessmentType === 'startup' ? 'Profilo Startup' : 'Profilo Progetto'}
                      </h4>
                      {(() => {
                        const fd = detail.formData as Record<string, string | string[]>;
                        const fields: Array<{ label: string; value: string }> = [];
                        if (fd.industry) fields.push({ label: 'Settore', value: String(fd.industry) });
                        if (fd.companySize) fields.push({ label: 'Dimensione', value: String(fd.companySize) });
                        if (fd.role) fields.push({ label: 'Ruolo', value: String(fd.role) });
                        if (fd.geography) fields.push({ label: 'Area', value: Array.isArray(fd.geography) ? fd.geography.join(', ') : String(fd.geography) });
                        // Startup
                        if (fd.startupStage) fields.push({ label: 'Fase', value: String(fd.startupStage) });
                        if (fd.sector) fields.push({ label: 'Settore', value: String(fd.sector) });
                        if (fd.founderBackground) fields.push({ label: 'Background fondatore', value: String(fd.founderBackground) });
                        // Project
                        if (fd.projectType) fields.push({ label: 'Tipo progetto', value: String(fd.projectType) });
                        if (fd.budget) fields.push({ label: 'Budget', value: String(fd.budget) });
                        if (fd.timeline) fields.push({ label: 'Timeline', value: String(fd.timeline) });
                        return (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                            {fields.map((f, i) => (
                              <div key={i}>
                                <span className="text-gray-500">{f.label}:</span>
                                <span className="ml-2 font-medium">{f.value}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Scores - adaptive per type */}
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Punteggi per Area</h4>
                      <div className="space-y-2">
                        {(detail.assessmentType || 'automation') === 'automation' ? (
                          <>
                            <CategoryScore label="Inserimento Dati" score={Number(detail.scores.dataEntry) || 0} />
                            <CategoryScore label="Vendite/Marketing" score={Number(detail.scores.salesMarketing) || 0} />
                            <CategoryScore label="Contabilità" score={Number(detail.scores.accounting) || 0} />
                            <CategoryScore label="Gestione Progetti" score={Number(detail.scores.projectMgmt) || 0} />
                            <CategoryScore label="Supply Chain" score={Number(detail.scores.supplyChain) || 0} />
                          </>
                        ) : detail.scores.dimensions && Array.isArray(detail.scores.dimensions) ? (
                          (detail.scores.dimensions as Array<{ name: string; score: number }>).map((dim, i) => (
                            <CategoryScore key={i} label={dim.name} score={Math.round((dim.score / 100) * 5)} />
                          ))
                        ) : (
                          <p className="text-sm text-gray-500">Punteggi dettagliati non disponibili</p>
                        )}
                      </div>
                    </div>

                    {/* Metrics - only show automation-specific metrics when available */}
                    {((detail.metrics.estimatedHoursWasted as number) > 0 || (detail.metrics.potentialSavingsPercent as number) > 0) && (
                    <div className="grid grid-cols-3 gap-3 lg:gap-4">
                      <div className="bg-red-50 rounded-lg p-3 lg:p-4 text-center">
                        <div className="text-xl lg:text-2xl font-bold text-red-600">
                          {(detail.metrics.estimatedHoursWasted as number) || 0}h
                        </div>
                        <div className="text-xs lg:text-sm text-red-700">Ore Perse/Sett</div>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-3 lg:p-4 text-center">
                        <div className="text-xl lg:text-2xl font-bold text-orange-600">
                          €{budgetEstimate?.wastedCost.toLocaleString()}
                        </div>
                        <div className="text-xs lg:text-sm text-orange-700">Costo Annuale</div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 lg:p-4 text-center">
                        <div className="text-xl lg:text-2xl font-bold text-green-600">
                          {(detail.metrics.potentialSavingsPercent as number) || 0}%
                        </div>
                        <div className="text-xs lg:text-sm text-green-700">Risparmio Potenziale</div>
                      </div>
                    </div>
                    )}

                    {/* Token Usage / Cost Tracking */}
                    {detail.tokenUsage && (
                      <div className="bg-purple-50 rounded-lg p-4">
                        <h4 className="font-medium text-purple-900 mb-3">Costo AI Assessment</h4>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-center">
                          <div>
                            <div className="text-lg font-bold text-purple-600">
                              {detail.tokenUsage.promptTokens.toLocaleString()}
                            </div>
                            <div className="text-xs text-purple-700">Token Input</div>
                          </div>
                          <div>
                            <div className="text-lg font-bold text-purple-600">
                              {detail.tokenUsage.completionTokens.toLocaleString()}
                            </div>
                            <div className="text-xs text-purple-700">Token Output</div>
                          </div>
                          <div>
                            <div className="text-lg font-bold text-purple-600">
                              {detail.tokenUsage.totalTokens.toLocaleString()}
                            </div>
                            <div className="text-xs text-purple-700">Token Totali</div>
                          </div>
                          <div>
                            <div className="text-lg font-bold text-purple-600">
                              ${detail.tokenUsage.estimatedCostUsd.toFixed(4)}
                            </div>
                            <div className="text-xs text-purple-700">Costo Stimato</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Pain Points & Quick Wins */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-white rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">Criticità</h4>
                        <ul className="space-y-1">
                          {detail.painPoints.map((p, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                              <span className="text-red-500">!</span>
                              {p}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">Azioni Rapide</h4>
                        <ul className="space-y-1">
                          {detail.quickWins.map((w, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                              <span className="text-green-500">+</span>
                              {w}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </>
                )}

                {activeTab === 'intelligence' && (
                  <>
                    {/* Sales Intelligence Tab */}

                    {/* Software Situation */}
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Situazione Software</h4>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <SignalPill active={intel.softwareSituation.hasCustomSoftware} label="Software Custom" />
                        <SignalPill active={intel.softwareSituation.hasLegacySystems} label="Sistemi Legacy" />
                        <SignalPill active={intel.softwareSituation.multipleDisconnectedTools} label="Tool Scollegati" />
                      </div>
                      {intel.softwareSituation.toolsMentioned.length > 0 && (
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Tool menzionati:</span>{' '}
                          {intel.softwareSituation.toolsMentioned.join(', ')}
                        </div>
                      )}
                    </div>

                    {/* Intent Signals */}
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Segnali di Intent</h4>
                      <div className="flex flex-wrap gap-2">
                        <SignalPill active={intel.intentSignals.lookingToDevelop} label="Vuole Sviluppare" />
                        <SignalPill active={intel.intentSignals.wantsConsolidation} label="Vuole Consolidare" />
                        <SignalPill active={intel.intentSignals.needsMobileApp} label="Serve App Mobile" />
                        <SignalPill active={intel.intentSignals.needsIntegrations} label="Serve Integrazione" />
                        <SignalPill active={intel.intentSignals.expressedFrustration} label="Frustrato" />
                      </div>
                    </div>

                    {/* AI Status */}
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Stato AI</h4>
                      <div className="flex items-center gap-4 mb-3">
                        <AIReadinessBadge level={intel.aiStatus.aiReadiness} />
                        {intel.aiStatus.privacyConcerns && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">
                            Preoccupato Privacy
                          </span>
                        )}
                      </div>
                      {intel.aiStatus.currentlyUsingAi && intel.aiStatus.aiToolsMentioned.length > 0 && (
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">AI in uso:</span>{' '}
                          {intel.aiStatus.aiToolsMentioned.join(', ')}
                        </div>
                      )}
                      {intel.aiStatus.privacyConcerns && (
                        <div className="mt-2 p-2 bg-blue-50 rounded text-sm text-blue-800">
                          <strong>Talking point:</strong> Spiegare che l'AI può essere usata in modo privato,
                          on-premise, rispettando GDPR e qualsiasi compliance. Possibilità di modelli custom.
                        </div>
                      )}
                    </div>

                    {/* Budget Analysis */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Analisi Budget</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                        <div className="bg-white rounded p-3 text-center">
                          <div className="text-lg font-bold text-red-600">
                            €{budgetEstimate?.wastedCost.toLocaleString()}/anno
                          </div>
                          <div className="text-xs text-gray-500">Costo Inefficienze</div>
                        </div>
                        <div className="bg-white rounded p-3 text-center">
                          <div className="text-lg font-bold text-blue-600">
                            {budgetEstimate?.suggestedBudget}
                          </div>
                          <div className="text-xs text-gray-500">Budget Suggerito</div>
                        </div>
                        <div className="bg-white rounded p-3 text-center">
                          <div className="text-lg font-bold text-green-600">
                            {budgetEstimate?.roi}
                          </div>
                          <div className="text-xs text-gray-500">ROI Stimato</div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <SignalPill active={intel.budgetSignals.mentionedBudget} label="Ha parlato di budget" />
                        <SignalPill active={intel.budgetSignals.priceSensitive} label="Sensibile al prezzo" />
                        <SignalPill active={intel.budgetSignals.failedInvestments} label="Investimenti falliti" />
                      </div>
                    </div>

                    {/* Decision Making (New Phase 3) */}
                    {intel.decisionMaking && (
                      <div className="bg-white rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">Processo Decisionale</h4>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className={`w-3 h-3 rounded-full ${intel.decisionMaking.identified ? 'bg-green-500' : 'bg-gray-300'}`} />
                            <span className="text-sm text-gray-600">
                              Decisore: {intel.decisionMaking.identified ? intel.decisionMaking.role : 'Non identificato'}
                            </span>
                          </div>
                          {intel.decisionMaking.isCurrentContact && (
                            <span className="inline-block px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                              Contatto attuale è il decisore
                            </span>
                          )}
                          {intel.decisionMaking.otherStakeholders && intel.decisionMaking.otherStakeholders.length > 0 && (
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">Altri coinvolti:</span> {intel.decisionMaking.otherStakeholders.join(', ')}
                            </div>
                          )}
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Processo di approvazione:</span> {intel.decisionMaking.approvalProcess}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Trigger Event (New Phase 3) */}
                    {intel.triggerEvent && (
                      <div className={`rounded-lg p-4 ${intel.triggerEvent.hasUrgency ? 'bg-orange-50' : 'bg-white'}`}>
                        <h4 className="font-medium text-gray-900 mb-3">Evento Scatenante</h4>
                        <div className="space-y-2">
                          {intel.triggerEvent.hasUrgency && (
                            <span className="inline-block px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-medium">
                              URGENTE
                            </span>
                          )}
                          {intel.triggerEvent.trigger && (
                            <p className="text-sm text-gray-700">"{intel.triggerEvent.trigger}"</p>
                          )}
                          {intel.triggerEvent.deadline && (
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">Deadline:</span> {intel.triggerEvent.deadline}
                            </div>
                          )}
                          <SignalPill active={intel.triggerEvent.externalPressure} label="Pressione esterna" />
                        </div>
                      </div>
                    )}

                    {/* Past Attempts (New Phase 3) */}
                    {intel.pastAttempts && intel.pastAttempts.hasPreviousFailures && (
                      <div className="bg-yellow-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">Tentativi Precedenti</h4>
                        <div className="space-y-2">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                            intel.pastAttempts.skepticismLevel === 'high' ? 'bg-red-100 text-red-800' :
                            intel.pastAttempts.skepticismLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            Scetticismo: {intel.pastAttempts.skepticismLevel}
                          </span>
                          {intel.pastAttempts.failureReasons && intel.pastAttempts.failureReasons.length > 0 && (
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">Motivi fallimento:</span>
                              <ul className="list-disc list-inside ml-2">
                                {intel.pastAttempts.failureReasons.map((r, i) => (
                                  <li key={i}>{r}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Implementation Capacity (New Phase 3) */}
                    {intel.implementationCapacity && (
                      <div className="bg-white rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">Capacità Implementativa</h4>
                        <div className="flex flex-wrap gap-2">
                          <SignalPill active={intel.implementationCapacity.hasInternalResources} label="Risorse interne" />
                          <SignalPill active={intel.implementationCapacity.needsFullService} label="Serve supporto completo" />
                        </div>
                        {intel.implementationCapacity.technicalLead && (
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">Referente tecnico:</span> {intel.implementationCapacity.technicalLead}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Success Metrics (New Phase 3) */}
                    {intel.successMetrics && intel.successMetrics.length > 0 && (
                      <div className="bg-white rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">Metriche di Successo</h4>
                        <ul className="space-y-1">
                          {intel.successMetrics.map((metric, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                              <span className="text-primary-600">✓</span>
                              {metric}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Suggested Solutions */}
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Soluzioni Suggerite (Tech Stack)</h4>
                      <div className="space-y-2">
                        {suggestedSolutions.map((sol, i) => (
                          <div key={i} className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                            <span className="flex-shrink-0 w-5 h-5 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xs font-medium">
                              {i + 1}
                            </span>
                            <span className="text-sm text-gray-700">{sol}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 text-xs text-gray-500">
                        Tecnologie: React, React Native, PostgreSQL, Redis, S3, SQS, AWS, K8s, Serverless, Python AI/ML
                      </div>
                    </div>

                    {/* Talking Points */}
                    <div className="bg-green-50 rounded-lg p-4">
                      <h4 className="font-medium text-green-900 mb-3">Punti Chiave per la Vendita</h4>
                      <ul className="space-y-2">
                        {intel.keyTalkingPoints.map((point, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-green-800">
                            <span className="text-green-600">→</span>
                            {point}
                          </li>
                        ))}
                      </ul>
                      {intel.recommendedApproach && (
                        <div className="mt-3 p-2 bg-green-100 rounded">
                          <span className="font-medium text-green-900">Approccio consigliato:</span>{' '}
                          <span className="text-green-800">{intel.recommendedApproach}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {activeTab === 'followup' && detail && (
                  <>
                    {/* Follow-up Questions - LLM Generated */}
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Domande di Follow-up</h4>
                      <p className="text-sm text-gray-500 mb-4">
                        Domande generate dall'AI basate sulla conversazione
                      </p>
                      <div className="space-y-2">
                        {(intel.followUpQuestions || []).map((q, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                            <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                              {i + 1}
                            </span>
                            <span className="text-sm text-blue-900">{q}</span>
                          </div>
                        ))}
                        {(!intel.followUpQuestions || intel.followUpQuestions.length === 0) && (
                          <div className="text-center text-gray-500 py-4">
                            Nessuna domanda di follow-up generata
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Pain Points for Sales - LLM Generated */}
                    {intel.painPointsForSales && intel.painPointsForSales.length > 0 && (
                      <div className="bg-red-50 rounded-lg p-4">
                        <h4 className="font-medium text-red-900 mb-3">Pain Points da Sottolineare</h4>
                        <ul className="space-y-2">
                          {intel.painPointsForSales.map((p, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-red-800">
                              <span className="text-red-600 font-bold">!</span>
                              {p}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Urgency Factors - LLM Generated */}
                    {intel.urgencyFactors && intel.urgencyFactors.length > 0 && (
                      <div className="bg-orange-50 rounded-lg p-4">
                        <h4 className="font-medium text-orange-900 mb-3">Fattori di Urgenza</h4>
                        <ul className="space-y-2">
                          {intel.urgencyFactors.map((f, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-orange-800">
                              <span className="text-orange-600">⚡</span>
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Objection Handlers - LLM Generated */}
                    {intel.objectionHandlers && intel.objectionHandlers.length > 0 && (
                      <div className="bg-white rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">Gestione Obiezioni</h4>
                        <div className="space-y-3">
                          {intel.objectionHandlers.map((oh, i) => (
                            <div key={i} className="border rounded-lg overflow-hidden">
                              <div className="bg-gray-100 px-3 py-2">
                                <span className="text-sm font-medium text-gray-700">
                                  ❌ "{oh.objection}"
                                </span>
                              </div>
                              <div className="px-3 py-2 bg-green-50">
                                <span className="text-sm text-green-800">
                                  ✓ {oh.response}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* No data message */}
                    {!intel.followUpQuestions?.length && !intel.painPointsForSales?.length &&
                     !intel.urgencyFactors?.length && !intel.objectionHandlers?.length && (
                      <div className="bg-yellow-50 rounded-lg p-6 text-center">
                        <p className="text-yellow-800 font-medium">Dati non disponibili</p>
                        <p className="text-yellow-600 text-sm mt-1">
                          Questi insight vengono generati dall'AI durante la conversazione.
                          Assessment più vecchi potrebbero non averli.
                        </p>
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'conversation' && (
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-4">Trascritto Conversazione</h4>
                    {detail.conversation && detail.conversation.length > 0 ? (
                      <div className="space-y-4 max-h-[600px] overflow-y-auto">
                        {detail.conversation.map((msg, i) => (
                          <div
                            key={i}
                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[80%] rounded-lg p-3 ${
                                msg.role === 'user'
                                  ? 'bg-primary-100 text-primary-900'
                                  : 'bg-gray-100 text-gray-900'
                              }`}
                            >
                              <div className="text-xs text-gray-500 mb-1">
                                {msg.role === 'user' ? 'Cliente' : 'Consulente'}
                              </div>
                              <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 py-8">
                        Conversazione non disponibile per questo assessment
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'violations' && (
                  <div className="space-y-4">
                    {detail.violations && detail.violations.totalCount > 0 ? (
                      <>
                        {/* Risk Score Card */}
                        <div className={`rounded-lg p-4 ${
                          detail.violations.flagged
                            ? 'bg-red-50 border-2 border-red-300'
                            : 'bg-yellow-50 border border-yellow-200'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-gray-900">Punteggio Rischio</h4>
                              <p className="text-sm text-gray-600">
                                {detail.violations.flagged
                                  ? 'Utente segnalato per comportamento sospetto'
                                  : 'Alcune violazioni rilevate'}
                              </p>
                            </div>
                            <div className="text-right">
                              <div className={`text-3xl font-bold ${
                                detail.violations.riskScore >= 50 ? 'text-red-600' : 'text-yellow-600'
                              }`}>
                                {detail.violations.riskScore}
                              </div>
                              <div className="text-xs text-gray-500">/ 100</div>
                            </div>
                          </div>
                        </div>

                        {/* Violations by Type */}
                        <div className="bg-white rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 mb-3">Violazioni per Tipo</h4>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <div className="text-center p-3 bg-gray-50 rounded">
                              <div className="text-2xl font-bold text-red-600">
                                {detail.violations.byType.jailbreak}
                              </div>
                              <div className="text-xs text-gray-500">Jailbreak</div>
                            </div>
                            <div className="text-center p-3 bg-gray-50 rounded">
                              <div className="text-2xl font-bold text-red-600">
                                {detail.violations.byType.harassment}
                              </div>
                              <div className="text-xs text-gray-500">Molestie</div>
                            </div>
                            <div className="text-center p-3 bg-gray-50 rounded">
                              <div className="text-2xl font-bold text-yellow-600">
                                {detail.violations.byType.off_topic}
                              </div>
                              <div className="text-xs text-gray-500">Fuori Tema</div>
                            </div>
                            <div className="text-center p-3 bg-gray-50 rounded">
                              <div className="text-2xl font-bold text-gray-500">
                                {detail.violations.byType.spam}
                              </div>
                              <div className="text-xs text-gray-500">Spam</div>
                            </div>
                          </div>
                        </div>

                        {/* Violation Log */}
                        <div className="bg-white rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 mb-3">Log Violazioni</h4>
                          <div className="space-y-2 max-h-[400px] overflow-y-auto">
                            {detail.violations.violations.map((v, i) => (
                              <div
                                key={i}
                                className={`p-3 rounded border-l-4 ${
                                  v.severity === 3
                                    ? 'bg-red-50 border-red-500'
                                    : v.severity === 2
                                    ? 'bg-yellow-50 border-yellow-500'
                                    : 'bg-gray-50 border-gray-300'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <ViolationTypeBadge type={v.type} />
                                  <span className="text-xs text-gray-400">
                                    {new Date(v.timestamp).toLocaleString('it-IT')}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700 break-words">
                                  "{v.message}"
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="bg-green-50 rounded-lg p-8 text-center">
                        <div className="text-green-600 text-4xl mb-2">✓</div>
                        <p className="text-green-800 font-medium">Nessuna violazione rilevata</p>
                        <p className="text-green-600 text-sm">Questo utente ha un comportamento regolare</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'copilot' && (
                  <div className="h-[600px]">
                    <CopilotChat
                      assessmentId={detail.id}
                      leadName={detail.lead.name}
                      companyName={detail.lead.company}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-lg p-8 text-center text-gray-500">
                Seleziona un assessment per vedere i dettagli
              </div>
            )}
          </div>
        </div>
      </main>
      )}
    </div>
  );
}
